import { find } from 'lodash';
import constants from '../../constants';
import { GFPlan, GFUser } from '../../models/models';
import { hasStripeSubscription } from '../../reducers/userReducer';

interface SelectedPlan {
  plan: GFPlan,
  overridePaymentPlanStudents: boolean,
}

interface PlanChange {
  isNew: boolean,
  isCurrent: boolean,
  isUpgrade: boolean,
  isDowngrade: boolean,
  userCurrentPlan: GFPlan,
}

const selectPaymentPlan = (planRecurringType: string, paymentPlanStudents: number, user?: GFUser): SelectedPlan => {
  if (user && hasStripeSubscription(user)) {
    const paymentPlan = find(constants.paymentPlanObjectsByID, { id: user.paymentPlan });

    if (paymentPlan) {
      return {
        plan: { ...paymentPlan, quantity: user.paymentQuantity },
        overridePaymentPlanStudents: true,
      };
    }
  }

  const masterPlanName = `master${planRecurringType}_3`;

  let plan = constants.paymentPlanObjectsByID[masterPlanName] || constants.paymentPlanObjectsByID.masterAnnual_3;
  const quantity = calculateQuantity(plan, paymentPlanStudents);

  return {
    plan: { ...plan, quantity },
    overridePaymentPlanStudents: false,
  };
};

const calculateQuantity = (plan: GFPlan, paymentPlanStudents: number) => {
  if (isHomeschool(plan)) {
    return plan.quantity;
  }

  const quantity = paymentPlanStudents / plan.size;

  if (quantity < plan.quantity) {
    return plan.quantity;
  }

  return Math.ceil(quantity);
}

const isHomeschool = (plan: GFPlan): boolean => {
  return plan.id === 'GF-Tier2-Annual-5';
}

const totalPrice = (plan: GFPlan): string => {
  return (plan.quantity * plan.price / 100).toFixed(2);
}

const findUserCurrentPlan = (user: GFUser) => {
  const paymentPlan = user.paymentPlan;
  const userCurrentPlan = find(constants.paymentPlanObjectsByID, plan => {
    return plan.id === paymentPlan;
  });

  if (!userCurrentPlan) {
    console.error(
      'could not find matching payment plan, assuming they are cancelled.'
    );
    return { ...constants.paymentPlanObjectsByID.cancelled };
  } else {
    return { ...userCurrentPlan, quantity: user.paymentQuantity };
  }
}

const planChange = (user: GFUser, selectedPlan: GFPlan, stripeSubscription: boolean): PlanChange => {
  const userCurrentPlan = findUserCurrentPlan(user);

  if (! stripeSubscription) {
    return {
      isNew: true,
      isCurrent: false,
      isUpgrade: false,
      isDowngrade: false,
      userCurrentPlan,
    }
  }

  const currentStudents = userCurrentPlan.size * userCurrentPlan.quantity;
  const selectedStudents = selectedPlan.size * selectedPlan.quantity;

  if (currentStudents === selectedStudents && userCurrentPlan.time === selectedPlan.time) {
    return {
      isNew: false,
      isCurrent: true,
      isUpgrade: false,
      isDowngrade: false,
      userCurrentPlan,
    }
  }

  if (currentStudents === selectedStudents && userCurrentPlan.time !== selectedPlan.time && userCurrentPlan.time !== 'Annual') {
    return {
      isNew: false,
      isCurrent: false,
      isUpgrade: true,
      isDowngrade: false,
      userCurrentPlan,
    }
  }

  if (currentStudents === selectedStudents && userCurrentPlan.time !== selectedPlan.time && userCurrentPlan.time === 'Annual') {
    return {
      isNew: false,
      isCurrent: false,
      isUpgrade: false,
      isDowngrade: true,
      userCurrentPlan,
    }
  }

  if (currentStudents > selectedStudents) {
    return {
      isNew: false,
      isCurrent: false,
      isUpgrade: false,
      isDowngrade: true,
      userCurrentPlan,
    }
  }

  return {
    isNew: false,
    isCurrent: false,
    isUpgrade: true,
    isDowngrade: false,
    userCurrentPlan,
  }
}

export default {
  selectPaymentPlan,
  calculateQuantity,
  isHomeschool,
  totalPrice,
  planChange,
};
